








































































































import { Vue, Component, Prop } from 'vue-property-decorator';
import { Bar } from 'vue-chartjs/legacy';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  ChartOptions,
  ScriptableScaleContext,
} from 'chart.js';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(isoWeek);

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);
import { SendIcon, PlusIcon, DollarSignIcon } from 'vue-feather-icons';
import { IAgency } from 'admiral-core';

interface WeekData {
  quantity: number[];
  value: number[];
}

interface WeeklyData {
  days: string[];
  neu: WeekData;
  eingereicht: WeekData;
  policiert: WeekData;
}

interface WeeklyComparisonData {
  currentWeek: WeeklyData;
  lastWeek: WeeklyData;
}

@Component({
  components: {
    Bar,
    SendIcon,
    PlusIcon,
    DollarSignIcon,
  },
})
export default class NewPoliciesCard extends Vue {
  @Prop({ type: String, required: true }) title!: string;
  @Prop({ type: String, required: true }) subtitle!: string;
  @Prop({ type: Number, required: false, default: 180 }) height!: number;
  @Prop({ type: Object, required: true }) weeklyComparisonData!: WeeklyComparisonData;
  @Prop({ type: Object, required: false }) agency!: IAgency;

  currentMetric: 'quantity' | 'value' = 'quantity';
  currentCategory: 'neu' | 'eingereicht' | 'policiert' = 'neu';
  isDropdownOpen = false;

  setCategory(category: 'neu' | 'eingereicht' | 'policiert') {
    this.currentCategory = category;
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  selectMetric(metric: 'quantity' | 'value') {
    this.currentMetric = metric;
    this.isDropdownOpen = false;
  }

  getTotalForCategory(category: 'neu' | 'eingereicht' | 'policiert'): string {
    if (
      !this.weeklyComparisonData ||
      !this.weeklyComparisonData.currentWeek ||
      !this.weeklyComparisonData.currentWeek[category] ||
      !this.weeklyComparisonData.currentWeek[category][this.currentMetric]
    ) {
      return '0';
    }

    const total = this.weeklyComparisonData.currentWeek[category][this.currentMetric].reduce(
      (sum: number, value: number) => sum + value,
      0,
    );

    return this.currentMetric === 'value'
      ? total.toLocaleString('de-DE')
      : total.toLocaleString('de-DE') + ' Stk.';
  }

  weekComparisonPercentage() {
    const currentArray =
      this.weeklyComparisonData.currentWeek[this.currentCategory][this.currentMetric];
    const lastArray = this.weeklyComparisonData.lastWeek[this.currentCategory][this.currentMetric];

    const today = dayjs().isoWeekday() - 1;

    const currentTotal = currentArray
      .slice(0, today + 1)
      .reduce((sum: number, val: number) => sum + val, 0);

    const lastTotal = lastArray
      .slice(0, today + 1)
      .reduce((sum: number, val: number) => sum + val, 0);

    let percentage: number;

    if (lastTotal === 0 && currentTotal > 0) {
      percentage = 100;
    } else if (lastTotal === 0 && currentTotal === 0) {
      percentage = 0;
    } else {
      percentage = ((currentTotal - lastTotal) / lastTotal) * 100;
    }

    const rounded = Math.round(percentage * 10) / 10;
    const badgeClass =
      rounded > 0 ? 'bg-light-success' : rounded < 0 ? 'bg-light-danger' : 'bg-light-secondary';

    return {
      percentage: `${rounded > 0 ? '+' : ''}${rounded} %`,
      badgeClass,
    };
  }

  get chartData() {
    const category = this.currentCategory;
    let currentWeekColor, lastWeekColor;

    switch (category) {
      case 'policiert':
        currentWeekColor = 'rgba(40, 199, 111)';
        lastWeekColor = 'rgba(40, 199, 111, 0.36)';
        break;
      case 'eingereicht':
        currentWeekColor = 'rgba(255, 165, 0)';
        lastWeekColor = 'rgba(255, 165, 0, 0.36)';
        break;
      case 'neu':
      default:
        currentWeekColor = 'rgba(58, 123, 199)';
        lastWeekColor = 'rgba(58, 123, 199, 0.36)';
        break;
    }

    return {
      labels: this.weeklyComparisonData.lastWeek.days,
      datasets: [
        {
          label: 'Letzte Woche',
          backgroundColor: lastWeekColor,
          data: this.weeklyComparisonData.lastWeek[category][this.currentMetric],
          borderRadius: 6,
        },
        {
          label: 'Diese Woche',
          backgroundColor: currentWeekColor,
          data: this.weeklyComparisonData.currentWeek[category][this.currentMetric],
          borderRadius: 6,
        },
      ],
    };
  }

  chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          drawBorder: false,
          display: false,
        },
        ticks: {
          font: (ctx: ScriptableScaleContext) => ({
            weight: ctx.index === dayjs().isoWeekday() - 1 ? 'bold' : 'normal',
            size: 14,
          }),
        },
      },
      y: {
        min: 0,
        title: { display: false },
        ticks: {
          display: false,
        },
        grid: {
          drawBorder: false,
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };
}
